import React, { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";

const brandBlue = "#263F86";
const borderColor = "#EEF0F4";

// `embedded` drops the grey box and heading, for pages that supply their own.
function Practice({ viewUserId = null, viewUserName = "", embedded = false }) {
  const [assessments, setAssessments] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleViewAttempts = (assessmentId, assessmentName) => {
    navigate("/attempt", {
      state: {
        assessmentId,
        assessmentName,
        userId: viewUserId || undefined,
        userName: viewUserName || undefined,
      },
    });
  };

  useEffect(() => {
    // An admin looking at a learner's attempts passes that learner's id in.
    if (viewUserId) {
      setUserId(viewUserId);
      return;
    }

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && parsedUser.user_id) {
          setUserId(parsedUser.user_id);
        }
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
        setError("Failed to load user information.");
      }
    }
  }, [viewUserId]);

  useEffect(() => {
    const fetchAssessments = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API}/historyAssessment?userId=${userId}`
        );
        const data = await response.json();

        if (data.success) {
          setAssessments(data.data);
        } else {
          setError(data.message || "Failed to fetch assessments.");
        }
      } catch (error) {
        console.error("Error fetching assessments:", error);
        setError("An error occurred while fetching assessments.");
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, [userId]);

  return (
    <div
      className="h-100"
      style={{
        fontFamily: "Manrope-Regular",
        ...(embedded
          ? {}
          : {
              backgroundColor: "#F7F7F7",
              borderRadius: "10px",
              padding: "20px",
            }),
      }}
    >
      {!embedded && (
        <h5
          className="text-start fw-bold formal-text mb-4"
          style={{ fontFamily: "Manrope-Regular" }}
        >
          Practice Assessment
        </h5>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {loading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" style={{ color: brandBlue }} />
          <span style={{ color: "#526079", fontSize: "14px" }}>Loading...</span>
        </div>
      )}

      {!loading && !error && assessments.length === 0 && (
        <p
          className="text-center mb-0 py-4"
          style={{ color: "#5F6B7A", fontWeight: 600, fontSize: "14px" }}
        >
          No practice assessments found.
        </p>
      )}

      {!loading && !error && assessments.length > 0 && (
        <div className="card-wrapper">
          {assessments.map((assessment) => (
            <div
              key={assessment.assessment_id}
              className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3 text-start"
              style={{
                background: "#ffffff",
                border: `1px solid ${borderColor}`,
                borderRadius: "14px",
                padding: "14px 18px",
                boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
              }}
            >
              <div
                className="flex-grow-1"
                style={{ fontWeight: 700, fontSize: "14px", color: "#1F2937" }}
              >
                {assessment.assessment_name}
              </div>

              <div style={{ fontSize: "13px", color: "#526079", fontWeight: 600 }}>
                <b style={{ color: brandBlue, fontWeight: 800 }}>
                  {assessment.no_of_attempts}/{assessment.max_attempt}
                </b>{" "}
                Attempt(s)
              </div>

              <button
                type="button"
                className="btn"
                onClick={() =>
                  handleViewAttempts(
                    assessment.assessment_id,
                    assessment.assessment_name
                  )
                }
                style={{
                  background: brandBlue,
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "8px 14px",
                  fontWeight: 700,
                  fontSize: "13px",
                  fontFamily: "Manrope-Regular",
                }}
              >
                <FaEye size={12} className="me-2" />
                View Attempts
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Practice;
